import { supabase } from '../supabase';
import {
  isOfficialCostCategory,
  normalizeReceiverCostConcept,
  OfficialCostCategory,
} from './receiver-costs';

export type ExporterCostSplitBasis = 'sales' | 'kg' | 'boxes';

export interface ExporterCostCategory {
  id: number;
  exporterId: number;
  name: string;
  externalCode: string | null;
  splitBasis: ExporterCostSplitBasis;
  displayOrder: number;
  archivedAt: string | null;
  /** Unmapped costs of this FM category fall into this category. */
  defaultForOfficialCategory: OfficialCostCategory | null;
}

/** A receiver cost name without an explicit exporter mapping. */
export interface UnclassifiedExporterCost {
  exporterReceiverId: number;
  receiverName: string;
  normalizedConcept: string;
  sampleOriginalName: string;
  officialCategory: OfficialCostCategory | null;
  lineCount: number;
  settlementCount: number;
  totalUsd: number;
  suggestedCategoryId: number | null;
  suggestionSource: 'other_receiver' | 'fm_default' | null;
}

export interface ExporterCostMappingChange {
  id: number;
  receiverName: string;
  sampleOriginalName: string;
  oldCategoryId: number | null;
  newCategoryId: number | null;
  changedByName: string | null;
  changedAt: string;
}

/** One revenue line's cost in one exporter category (null = Sin clasificar). */
export interface SettlementExporterCost {
  settlementId: number;
  settlementDetailId: number;
  categoryId: number | null;
  amountLocal: number;
  amountUsd: number;
}

export const EXPORTER_COST_SPLIT_BASIS_LABELS: Record<
  ExporterCostSplitBasis,
  string
> = {
  sales: 'Por venta',
  kg: 'Por kilo',
  boxes: 'Por caja',
};

export const UNCLASSIFIED_EXPORTER_COST_LABEL = 'Sin clasificar';

/** Window event fired after exporter cost mappings are saved. */
export const EXPORTER_COST_MAPPINGS_CHANGED_EVENT =
  'exporter-cost-mappings-changed';

/** First season with the exporter view (mirrors exporter_cost_view_min_season()). */
export const EXPORTER_COST_VIEW_MIN_SEASON = '2026-2027';

export const isExporterCostViewSeason = (season: string | null | undefined) =>
  Boolean(season) && season! >= EXPORTER_COST_VIEW_MIN_SEASON;

const mapCategoryRow = (row: any): ExporterCostCategory => ({
  id: Number(row.id),
  exporterId: Number(row.exporter_id),
  name: String(row.name),
  externalCode: row.external_code ?? null,
  splitBasis: row.split_basis,
  displayOrder: Number(row.display_order) || 0,
  archivedAt: row.archived_at ?? null,
  defaultForOfficialCategory: isOfficialCostCategory(
    row.default_for_official_category
  )
    ? row.default_for_official_category
    : null,
});

/**
 * Where an unmapped cost of this FM category currently lands: the exporter's
 * default category for it, or null ("Sin clasificar").
 */
export const getDefaultExporterCostCategory = (
  categories: ExporterCostCategory[],
  officialCategory: string | null | undefined
) =>
  categories.find(
    (category) =>
      !category.archivedAt &&
      category.defaultForOfficialCategory === officialCategory
  ) || null;

export const getCustomCostCategoriesEnabled = async (
  exporterId: number
): Promise<boolean> => {
  const { data, error } = await supabase
    .from('exporters')
    .select('custom_cost_categories_enabled')
    .eq('id', exporterId)
    .maybeSingle();

  if (error) throw error;
  return data?.custom_cost_categories_enabled === true;
};

export const getExporterCostCategories = async (
  exporterId: number,
  includeArchived = false
): Promise<ExporterCostCategory[]> => {
  let query = supabase
    .from('exporter_cost_categories')
    .select(
      'id, exporter_id, name, external_code, split_basis, display_order, archived_at, default_for_official_category'
    )
    .eq('exporter_id', exporterId)
    .order('display_order')
    .order('name');
  if (!includeArchived) query = query.is('archived_at', null);

  const { data, error } = await query;
  if (error) throw error;
  return (data || []).map(mapCategoryRow);
};

export const upsertExporterCostCategory = async (
  exporterId: number,
  category: {
    id?: number | null;
    name: string;
    externalCode?: string | null;
    splitBasis: ExporterCostSplitBasis;
    displayOrder?: number;
    defaultForOfficialCategory?: OfficialCostCategory | null;
  }
): Promise<ExporterCostCategory> => {
  const { data, error } = await supabase.rpc('upsert_exporter_cost_category', {
    p_exporter_id: exporterId,
    p_id: category.id ?? null,
    p_name: category.name,
    p_external_code: category.externalCode ?? null,
    p_split_basis: category.splitBasis,
    p_display_order: category.displayOrder ?? 0,
    p_default_for_official_category:
      category.defaultForOfficialCategory ?? null,
  });

  if (error) throw error;
  return mapCategoryRow(data);
};

export const archiveExporterCostCategory = async (
  categoryId: number
): Promise<void> => {
  const { error } = await supabase.rpc('archive_exporter_cost_category', {
    p_id: categoryId,
  });
  if (error) throw error;
};

/** Normalized receiver cost concept -> exporter category id. */
export const getReceiverCostExporterMappings = async (
  exporterId: number,
  exporterReceiverId: number
): Promise<Record<string, number>> => {
  const { data, error } = await supabase
    .from('receiver_cost_exporter_mappings')
    .select('normalized_concept, exporter_cost_category_id')
    .eq('exporter_id', exporterId)
    .eq('exporter_receiver_id', exporterReceiverId);

  if (error) throw error;
  return Object.fromEntries(
    (data || []).map((row: any) => [
      row.normalized_concept,
      Number(row.exporter_cost_category_id),
    ])
  );
};

/**
 * Saves exporter-category choices for receiver cost names. A null category
 * removes the mapping. Changes apply to every settlement from the first
 * exporter-view season onwards.
 */
export const setReceiverCostExporterCategories = async (
  exporterReceiverId: number,
  items: Array<{ name: string; categoryId: number | null }>
): Promise<void> => {
  const { error } = await supabase.rpc(
    'set_receiver_cost_exporter_categories',
    {
      p_exporter_receiver_id: exporterReceiverId,
      p_items: items.map((item) => ({
        name: item.name,
        categoryId: item.categoryId,
      })),
    }
  );
  if (error) throw error;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(EXPORTER_COST_MAPPINGS_CHANGED_EVENT));
  }
};

export const getSettlementExporterCosts = async (
  settlementIds: number[]
): Promise<SettlementExporterCost[]> => {
  if (settlementIds.length === 0) return [];
  const { data, error } = await supabase.rpc('get_settlement_exporter_costs', {
    p_settlement_ids: settlementIds,
  });

  if (error) throw error;
  return (data || []).map((row: any) => ({
    settlementId: Number(row.settlement_id),
    settlementDetailId: Number(row.settlement_detail_id),
    categoryId:
      row.exporter_cost_category_id === null
        ? null
        : Number(row.exporter_cost_category_id),
    amountLocal: Number(row.amount_local) || 0,
    amountUsd: Number(row.amount_usd) || 0,
  }));
};

/**
 * Exporter-standard cost totals per source group and settlement (drill-down),
 * keyed as `${groupId}:${settlementId}`, in settlement local currency.
 */
export const getSettlementSourceExporterCosts = async (
  settlementIds: number[]
): Promise<Map<string, number>> => {
  const totals = new Map<string, number>();
  if (settlementIds.length === 0) return totals;
  const { data, error } = await supabase.rpc(
    'get_settlement_source_exporter_costs',
    { p_settlement_ids: settlementIds }
  );

  if (error) throw error;
  (data || []).forEach((row: any) => {
    totals.set(
      `${row.source_settlement_group_id}:${row.settlement_id}`,
      Number(row.amount_local) || 0
    );
  });
  return totals;
};

export const getUnclassifiedExporterCosts = async (
  exporterId: number
): Promise<UnclassifiedExporterCost[]> => {
  const { data, error } = await supabase.rpc(
    'get_unclassified_exporter_costs',
    { p_exporter_id: exporterId }
  );

  if (error) throw error;
  return (data || []).map((row: any) => ({
    exporterReceiverId: Number(row.exporter_receiver_id),
    receiverName: String(row.receiver_name || ''),
    normalizedConcept: String(row.normalized_concept),
    sampleOriginalName: String(row.sample_original_name || ''),
    officialCategory: isOfficialCostCategory(row.official_category)
      ? row.official_category
      : null,
    lineCount: Number(row.line_count) || 0,
    settlementCount: Number(row.settlement_count) || 0,
    totalUsd: Number(row.total_usd) || 0,
    suggestedCategoryId:
      row.suggested_category_id === null
        ? null
        : Number(row.suggested_category_id),
    suggestionSource:
      row.suggestion_source === 'other_receiver' ||
      row.suggestion_source === 'fm_default'
        ? row.suggestion_source
        : null,
  }));
};

/** A receiver cost name seen by the exporter (any season). */
export interface ReceiverCostConcept {
  exporterReceiverId: number;
  receiverName: string;
  normalizedConcept: string;
  sampleOriginalName: string;
  officialCategory: OfficialCostCategory | null;
  lineCount: number;
  settlementCount: number;
  totalUsd: number;
  lastSeenAt: string | null;
  categoryId: number | null;
}

export const getReceiverCostConcepts = async (
  exporterId: number
): Promise<ReceiverCostConcept[]> => {
  const { data, error } = await supabase.rpc('get_receiver_cost_concepts', {
    p_exporter_id: exporterId,
  });

  if (error) throw error;
  return (data || []).map((row: any) => ({
    exporterReceiverId: Number(row.exporter_receiver_id),
    receiverName: String(row.receiver_name || ''),
    normalizedConcept: String(row.normalized_concept),
    sampleOriginalName: String(row.sample_original_name || ''),
    officialCategory: isOfficialCostCategory(row.official_category)
      ? row.official_category
      : null,
    lineCount: Number(row.line_count) || 0,
    settlementCount: Number(row.settlement_count) || 0,
    totalUsd: Number(row.total_usd) || 0,
    lastSeenAt: row.last_seen_at ?? null,
    categoryId:
      row.exporter_cost_category_id === null
        ? null
        : Number(row.exporter_cost_category_id),
  }));
};

export const getExporterCostMappingChanges = async (
  exporterId: number,
  limit = 100
): Promise<ExporterCostMappingChange[]> => {
  const { data, error } = await supabase
    .from('receiver_cost_exporter_mapping_changes')
    .select(
      'id, sample_original_name, old_category_id, new_category_id, changed_at, exporter_receivers(name), users(first_name, last_name)'
    )
    .eq('exporter_id', exporterId)
    .order('changed_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return (data || []).map((row: any) => ({
    id: Number(row.id),
    receiverName: String(row.exporter_receivers?.name || ''),
    sampleOriginalName: String(row.sample_original_name || ''),
    oldCategoryId:
      row.old_category_id === null ? null : Number(row.old_category_id),
    newCategoryId:
      row.new_category_id === null ? null : Number(row.new_category_id),
    changedByName: row.users
      ? `${row.users.first_name || ''} ${row.users.last_name || ''}`.trim() ||
        null
      : null,
    changedAt: String(row.changed_at),
  }));
};

export { normalizeReceiverCostConcept };
